import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, can } from '../../lib/api';
import type { Site } from '../../lib/types';
import { Banner, Button, Card, Field, Toast, input } from '../../lib/ui';
import { Modal } from '../../lib/Modal';
import { navigate } from '../../app';
import { t } from '../../lib/i18n';

/**
 * The two things about a site that no other screen could do (Doc 04 §10.3).
 *
 * Deliberately not the six sub-tabs the doc lists: the domain allowlist sits on the
 * blocking screen next to the events a missing entry drops, the weekly scan on the scan
 * screen, and API keys and the fallback regime are per tenant and live under Team. What
 * had no route into it at all was the name — written once in the wizard and frozen after,
 * so a typo was permanent — and deleting a site, which the backend has supported since
 * the first migration while the console offered no way to ask for it.
 *
 * Site status (`active`/`paused`/`archived`) is left out on purpose: PATCH accepts it, but
 * nothing in the backend reads it, so a switch here would only look like it did something.
 */
export function SettingsScreen({ site }: { site: Site }) {
  const qc = useQueryClient();
  const [name, setName] = useState(site.name);
  const [typed, setTyped] = useState('');
  const [asking, setAsking] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const rename = useMutation({
    mutationFn: () => api<Site>(`/v1/sites/${site.id}`, { method: 'PATCH', body: { name: name.trim() } }),
    onSuccess: (s) => {
      qc.setQueryData(['site', site.id], s);
      void qc.invalidateQueries({ queryKey: ['sites'] });
      setProblem(null);
      setDone(t('Name geändert.'));
    },
    onError: (e) => setProblem(
      e instanceof ApiError && e.fieldErrors['name']
        ? t('Der Name braucht mindestens ein und höchstens 120 Zeichen.')
        : t('Fehlgeschlagen.'),
    ),
  });

  const remove = useMutation({
    mutationFn: () => api(`/v1/sites/${site.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sites'] });
      navigate('/sites');
    },
    onError: () => { setAsking(false); setProblem(t('Fehlgeschlagen.')); },
  });

  const writable = can('site:update');
  const deletable = can('site:delete');
  const trimmed = name.trim();

  return (
    <div className="space-y-4">
      {done && <Toast message={done} onDone={() => setDone(null)} />}
      {problem && <Banner kind="error">{problem}</Banner>}

      <Card title={t('Allgemein')}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[260px] flex-1">
            <Field label={t('Name')} hint={t('Nur für die Konsole — Besucher sehen ihn nie.')}>
              <input
                className={input}
                value={name}
                maxLength={120}
                disabled={!writable}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
          </div>
          <Button
            disabled={!writable || trimmed === '' || trimmed === site.name || rename.isPending}
            onClick={() => rename.mutate()}
          >
            {t('Speichern')}
          </Button>
        </div>
        {/* The rest of what Doc 04 calls site settings is real, it is just not here — and
            someone who cannot find the domain allowlist assumes it does not exist. Rows,
            not a sentence: a sentence with three links in it translates badly and reads
            like an apology. */}
        <dl className="mt-4 space-y-1 text-xs text-[var(--color-muted)]">
          {[
            [t('Domains'), t('Blockieren & Anbieter'), `#/sites/${site.id}/blocking`],
            [t('Wöchentlicher Scan'), t('Scan'), `#/sites/${site.id}/scan`],
            [t('API-Schlüssel, Rückfall-Regime (ganzes Konto)'), t('Team'), '#/team'],
          ].map(([what, where, href]) => (
            <div key={href} className="flex flex-wrap gap-x-2">
              <dt>{what}</dt>
              <dd><a className="underline" href={href}>{where}</a></dd>
            </div>
          ))}
        </dl>
      </Card>

      {deletable && (
        <Card title={t('Gefahrenzone')}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="max-w-2xl text-[13px] leading-relaxed text-[var(--color-muted)]">
              {t('Nach dem Löschen wird die Konfiguration nicht mehr ausgeliefert: das Snippet auf {domain} blockiert dann alles nach seinen Startregeln, bis Sie es entfernen. Nachweise und Protokoll bleiben erhalten.',
                { domain: site.primary_domain })}
            </p>
            <Button variant="danger" onClick={() => { setTyped(''); setAsking(true); }}>
              {t('Website löschen')}
            </Button>
          </div>
        </Card>
      )}

      {/* Typed confirmation rather than a plain "are you sure": this is the one action in
          the console that takes a live banner off a customer's website, and the dialog for
          it should be impossible to click through by muscle memory. */}
      <Modal
        open={asking}
        title={t('Website löschen')}
        onClose={() => setAsking(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAsking(false)}>{t('Abbrechen')}</Button>
            <Button
              variant="danger"
              disabled={typed.trim() !== site.primary_domain || remove.isPending}
              onClick={() => remove.mutate()}
            >
              {t('Endgültig löschen')}
            </Button>
          </>
        }
      >
        <Field
          label={t('Tippen Sie {domain}, um zu bestätigen.', { domain: site.primary_domain })}
          hint={t('Danach verschwindet die Website aus der Übersicht und ihre Auswertung ist nicht mehr erreichbar.')}
        >
          <input className={input} value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
        </Field>
      </Modal>
    </div>
  );
}
